import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, CheckCircle, Factory, Building, Globe } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: <Building className="w-6 h-6" />,
      title: "Merkez Ofis",
      details: [
        "📍 Zuhuratbaba Mah. Gençler Cad. NO:58/1",
        "Bakırköy İSTANBUL",
        "📞 +90 212 671 28 70",
        "📧 info@mimalum.com"
      ]
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: "Asya Şube Ofisi",
      details: [
        "📍 Çavuşoğlu Mah. Yakacık Cad. No:128/2",
        "Kartal İSTANBUL/ASYA",
        "📧 info@mimalum.com"
      ]
    },
    {
      icon: <Factory className="w-6 h-6" />,
      title: "Fabrika",
      details: [
        "📍 Pelitli Mahallesi Tavşanlı Yolu Caddesi No: 123",
        "Gebze / KOCAELİ, Türkiye",
        "📧 info@mimalum.com"
      ]
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: "ABD Şube Ofisi",
      details: [
        "📍 361 South Bundy Drive",
        "Los Angeles, CA 90049",
        "📞 310-866-6497",
        "📧 info@mimalum.com"
      ]
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "İhracat Departmanı",
      details: [
        "📧 sales@mimalum.com",
        "📞 +90 212 671 28 70",
        "🌍 Uluslararası Satış & Dağıtım"
      ]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Bilgi Hattı",
      details: [
        "📞 +90 212 671 28 70",
        "📧 info@mimalum.com"
      ]
    }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={IMAGES.FEMALE_CALL_CENTER_1_272}
            alt="Müşteri Hizmetleri"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-transparent to-background" />

        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl font-bold mb-6"
          >
            Bize Ulaşın
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Alüminyum çözüm ihtiyaçlarınız için uzman ekibimizle iletişime geçin.
            Teknik destek, fiyat teklifi ve proje danışmanlığı için buradayız.
          </motion.p>
        </div>
      </section>

      {/* İletişim Ofisleri */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-primary">İletişim Noktalarımız</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Kişiselleştirilmiş destek ve çözümler için global ofislerimizle iletişime geçin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6 bg-card rounded-lg border hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mx-auto mb-4">
                  {info.icon}
                </div>
                <h3 className="font-semibold text-lg mb-3 text-primary">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, detailIndex) => {
                    const styledDetail = detail.replace(/(📍|📞|📧)/g, '<span class="text-primary">$1</span>');
                    return (
                      <p key={detailIndex} className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: styledDetail }} />
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* İletişim Formu & Bilgi */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* İletişim Formu */}
            <motion.div
              id="teklif-formu"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-8 text-primary">Mesaj Gönderin</h2>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-6 text-center"
                >
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Mesajınız Gönderildi!</h3>
                  <p className="text-green-700">Bize ulaştığınız için teşekkürler. 24 saat içinde size geri döneceğiz.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Ad Soyad *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        E-posta Adresi *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="ornek@sirket.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">
                        Telefon Numarası
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+90 (212) 123 45 67"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-2">
                        Şirket Adı
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Şirketinizin adı"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-2">
                      Konu *
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Talebinizin kısa açıklaması"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Mesaj *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Proje gereksinimlerinizi, miktarları, teknik özellikleri veya sorularınızı buraya yazabilirsiniz..."
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full text-lg py-6">
                    <Send className="w-5 h-5 mr-2" />
                    Mesajı Gönder
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Neden Mimalum */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-8 text-primary">Neden Mimalum?</h2>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="p-8 bg-primary/5 rounded-lg border border-primary/20"
              >
                <ul className="space-y-4 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span>46+ yıllık sektör deneyimi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span>Entegre üretim tesisleri</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span>Uzman teknik destek ekibi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span>Özel çözüm geliştirme</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span>Kalite güvencesi ve test süreçleri</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span>Rekabetçi fiyat ve hızlı teslimat</span>
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Alt Bilgi Bölümü */}
      <section className="py-20 bg-accent/5">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <Mail className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4 text-primary">Mesaj Gönderin</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Belirli bir sorunuz mu var veya teknik desteğe mi ihtiyacınız var? Uzman ekibimiz alüminyum çözüm gereksinimlerinizde size yardımcı olmaya hazır.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="px-8">
                <Mail className="w-5 h-5 mr-2" />
                Satış Ekibiyle İletişim
              </Button>
              <Button variant="outline" size="lg" className="px-8">
                Teknik Destek
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
